import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyEssay = {
    id: null,
    title: "",
    subtitle: "",
    excerpt: "",
    content: "",
    category: "overachieving-for-me",
    cover_image: "",
    featured: false,
    published: true,
    read_minutes: 5,
};

export default function Admin() {
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = useState("essays");
    const [essays, setEssays] = useState([]);
    const [editing, setEditing] = useState(null); // null = list; object = editing
    const [subs, setSubs] = useState([]);
    const [msgs, setMsgs] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = () => {
        api.get("/essays", { params: { published: undefined } })
            .then((r) => setEssays(r.data));
        api.get("/newsletter/subscribers").then((r) => setSubs(r.data)).catch(() => {});
        api.get("/contact/messages").then((r) => setMsgs(r.data)).catch(() => {});
    };

    useEffect(() => {
        if (user) load();
    }, [user]);

    if (loading) return <div className="pt-40 text-center italic text-2xl">…</div>;
    if (!user) return <Navigate to="/admin/login" replace />;

    const saveEssay = async () => {
        setSaving(true);
        try {
            const payload = { ...editing };
            delete payload.id;
            delete payload.slug;
            delete payload.created_at;
            delete payload.updated_at;
            if (editing.id) {
                await api.put(`/essays/${editing.id}`, payload);
                toast.success("Essay updated.");
            } else {
                await api.post("/essays", payload);
                toast.success("Essay published.");
            }
            setEditing(null);
            load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const deleteEssay = async (id) => {
        if (!window.confirm("Delete this essay? This cannot be undone.")) return;
        try {
            await api.delete(`/essays/${id}`);
            toast.success("Essay deleted.");
            load();
        } catch {
            toast.error("Delete failed.");
        }
    };

    return (
        <div className="pt-28 pb-24 min-h-screen" data-testid="admin-page">
            <div className="mx-auto max-w-[1400px] px-6 md:px-10">
                <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                    <div>
                        <p className="text-[11px] tracking-[0.32em] uppercase font-bold text-[rgb(var(--gold))]">
                            The Study / Admin
                        </p>
                        <h1 className="mt-2 text-5xl md:text-6xl tracking-tight text-[rgb(var(--teal))]">
                            Welcome back, {user.name || "Leah"}.
                        </h1>
                    </div>
                    <div className="flex gap-3">
                        <Link to="/" className="btn-ghost" data-testid="admin-view-site">View site →</Link>
                        <button onClick={logout} className="btn-ghost" data-testid="admin-logout">Sign out</button>
                    </div>
                </div>

                <div className="flex gap-2 border-b hairline mb-10">
                    {[
                        { key: "essays", label: `Essays (${essays.length})` },
                        { key: "subs", label: `Subscribers (${subs.length})` },
                        { key: "msgs", label: `Messages (${msgs.length})` },
                    ].map((t) => (
                        <button
                            key={t.key}
                            onClick={() => { setTab(t.key); setEditing(null); }}
                            className={`px-5 py-3 text-[11px] tracking-[0.28em] uppercase font-bold transition-colors ${
                                tab === t.key
                                    ? "text-[rgb(var(--orange))] border-b-2 border-[rgb(var(--orange))] -mb-px"
                                    : "text-[rgb(var(--ink-2))] hover:text-[rgb(var(--teal))]"
                            }`}
                            data-testid={`admin-tab-${t.key}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "essays" && !editing && (
                    <div>
                        <div className="mb-6">
                            <button
                                onClick={() => setEditing({ ...emptyEssay })}
                                className="btn-primary"
                                data-testid="admin-new-essay"
                            >
                                + New essay
                            </button>
                        </div>
                        <div className="border hairline">
                            {essays.length === 0 && (
                                <div className="p-10 text-center italic text-xl text-[rgb(var(--ink-2))]">
                                    No essays yet. Start one above.
                                </div>
                            )}
                            {essays.map((e) => (
                                <div
                                    key={e.id}
                                    className="grid grid-cols-12 gap-4 items-center p-5 border-b hairline last:border-b-0 hover:bg-[rgb(var(--paper-2))] transition-colors"
                                >
                                    <div className="col-span-12 md:col-span-6">
                                        <p className="text-2xl text-[rgb(var(--teal))]">{e.title}</p>
                                        <p className="text-sm text-[rgb(var(--ink-2))]">/{e.slug}</p>
                                    </div>
                                    <div className="col-span-6 md:col-span-3 text-xs uppercase tracking-widest text-[rgb(var(--gold))]">
                                        {CATEGORIES.find((c) => c.key === e.category)?.short}
                                    </div>
                                    <div className="col-span-6 md:col-span-1 text-xs">
                                        {e.featured && <span className="text-[rgb(var(--orange))]">★ Featured</span>}
                                        {!e.published && <span className="text-[rgb(var(--ink-2))]/50 block">draft</span>}
                                    </div>
                                    <div className="col-span-12 md:col-span-2 flex gap-3 justify-end">
                                        <button
                                            onClick={() => setEditing(e)}
                                            className="text-xs uppercase tracking-widest font-bold text-[rgb(var(--teal))] hover:text-[rgb(var(--orange))]"
                                            data-testid={`admin-edit-${e.slug}`}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteEssay(e.id)}
                                            className="text-xs uppercase tracking-widest font-bold text-[rgb(var(--ink-2))] hover:text-red-700"
                                            data-testid={`admin-delete-${e.slug}`}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "essays" && editing && (
                    <div className="max-w-4xl">
                        <div className="flex justify-between items-center mb-8">
                            <h2 className="text-3xl text-[rgb(var(--teal))]">
                                {editing.id ? "Edit essay" : "New essay"}
                            </h2>
                            <button
                                onClick={() => setEditing(null)}
                                className="text-xs uppercase tracking-widest text-[rgb(var(--ink-2))] hover:text-[rgb(var(--orange))]"
                                data-testid="admin-cancel-edit"
                            >
                                ← Back to list
                            </button>
                        </div>
                        <div className="space-y-6">
                            <div>
                                <label className="text-[10px] tracking-[0.28em] uppercase font-bold text-[rgb(var(--gold))]">Title</label>
                                <input
                                    className="input-underline mt-2 text-3xl"
                                    value={editing.title}
                                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                    data-testid="admin-essay-title"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] tracking-[0.28em] uppercase font-bold text-[rgb(var(--gold))]">Subtitle</label>
                                <input
                                    className="input-underline mt-2 italic text-xl"
                                    value={editing.subtitle}
                                    onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                                    data-testid="admin-essay-subtitle"
                                />
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-[10px] tracking-[0.28em] uppercase font-bold text-[rgb(var(--gold))]">Category</label>
                                    <select
                                        className="input-underline mt-2 bg-transparent"
                                        value={editing.category}
                                        onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                                        data-testid="admin-essay-category"
                                    >
                                        {CATEGORIES.map((c) => (
                                            <option key={c.key} value={c.key}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[10px] tracking-[0.28em] uppercase font-bold text-[rgb(var(--gold))]">Read minutes</label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={60}
                                        className="input-underline mt-2"
                                        value={editing.read_minutes}
                                        onChange={(e) => setEditing({ ...editing, read_minutes: Number(e.target.value) })}
                                        data-testid="admin-essay-minutes"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] tracking-[0.28em] uppercase font-bold text-[rgb(var(--gold))]">Cover image URL</label>
                                <input
                                    className="input-underline mt-2"
                                    value={editing.cover_image}
                                    onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
                                    data-testid="admin-essay-cover"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] tracking-[0.28em] uppercase font-bold text-[rgb(var(--gold))]">Excerpt</label>
                                <textarea
                                    rows={3}
                                    className="input-underline mt-2 resize-none"
                                    value={editing.excerpt}
                                    onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                                    data-testid="admin-essay-excerpt"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] tracking-[0.28em] uppercase font-bold text-[rgb(var(--gold))]">Content (double line-break between paragraphs)</label>
                                <textarea
                                    rows={16}
                                    className="input-underline mt-2 resize-y text-lg"
                                    value={editing.content}
                                    onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                                    data-testid="admin-essay-content"
                                />
                            </div>
                            <div className="flex flex-wrap gap-6 pt-2">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={editing.featured}
                                        onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                                        data-testid="admin-essay-featured"
                                    />
                                    <span className="text-sm uppercase tracking-widest text-[rgb(var(--ink))]">Feature on homepage</span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={editing.published}
                                        onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                                        data-testid="admin-essay-published"
                                    />
                                    <span className="text-sm uppercase tracking-widest text-[rgb(var(--ink))]">Published</span>
                                </label>
                            </div>
                            <div className="pt-6 flex gap-4">
                                <button
                                    onClick={saveEssay}
                                    disabled={saving}
                                    className="btn-primary disabled:opacity-50"
                                    data-testid="admin-essay-save"
                                >
                                    {saving ? "Saving…" : editing.id ? "Update essay" : "Publish essay"}
                                </button>
                                <button
                                    onClick={() => setEditing(null)}
                                    className="btn-ghost"
                                    data-testid="admin-essay-cancel"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "subs" && (
                    <div className="border hairline">
                        {subs.length === 0 ? (
                            <p className="p-10 italic text-xl text-[rgb(var(--ink-2))]">No subscribers yet.</p>
                        ) : (
                            subs.map((s) => (
                                <div key={s.id} className="p-4 border-b hairline last:border-0 flex justify-between">
                                    <span>{s.email}</span>
                                    <span className="text-xs text-[rgb(var(--ink-2))]/60">
                                        {new Date(s.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {tab === "msgs" && (
                    <div className="space-y-6">
                        {msgs.length === 0 && (
                            <p className="p-10 italic text-xl text-[rgb(var(--ink-2))]">No messages yet.</p>
                        )}
                        {msgs.map((m) => (
                            <div key={m.id} className="border hairline p-6">
                                <div className="flex flex-wrap justify-between gap-2 mb-3">
                                    <p className="text-xl text-[rgb(var(--teal))]">
                                        {m.name} <span className="text-[rgb(var(--ink-2))] text-base">·</span>{" "}
                                        <a href={`mailto:${m.email}`} className="text-[rgb(var(--orange))] text-base link-underline">{m.email}</a>
                                    </p>
                                    <span className="text-xs text-[rgb(var(--ink-2))]/60">
                                        {new Date(m.created_at).toLocaleString()}
                                    </span>
                                </div>
                                {m.subject && <p className="text-sm uppercase tracking-widest text-[rgb(var(--gold))]">{m.subject}</p>}
                                <p className="mt-4 whitespace-pre-wrap text-[rgb(var(--ink-2))] leading-relaxed">{m.message}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
